// src/pages/ManageSubscription.jsx
import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, AlertTriangle, Loader2, Home } from "lucide-react";

function ManageSubscription() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | cancelling | cancelled | error

  const handleCancel = async () => {
    setStatus("cancelling");
    try {
      const apiBase = import.meta.env.VITE_APP_BASE_URL?.replace(/\/$/, "");
      const res = await fetch(`${apiBase}/donations/subscription/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (res.ok) {
        setStatus("cancelled");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf9] px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl">Invalid link</CardTitle>
            <CardDescription>This link is not valid or has expired.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/")} className="mt-2">
              <Home className="mr-2 h-4 w-4" />
              Return home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className="min-h-screen bg-[#f7faf9] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg space-y-6">
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-6 text-center space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf4f2]">
              <CheckCircle className="h-7 w-7 text-[#9d1f1c]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#14201f]">
              Your monthly seva has been cancelled.
            </h1>
            <p className="text-sm leading-6 text-[#52615f]">
              Thank you for your support. 🙏
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-xs text-[#52615f] hover:underline"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7faf9] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-8 text-center space-y-5">
          <h1 className="text-2xl font-semibold text-[#14201f]">Manage your monthly seva</h1>
          <p className="text-sm leading-6 text-[#52615f]">
            You can stop your recurring contribution at any time. Your past support has been
            received with deep gratitude.
          </p>

          {status === "error" && (
            <div className="rounded-lg border border-[#f2d1d1] bg-[#fff5f5] p-4 text-sm text-[#9d1f1c]">
              We were unable to process your request just now. Please try again or write to us for
              assistance.
            </div>
          )}

          <button
            type="button"
            onClick={handleCancel}
            disabled={status === "cancelling"}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#9d1f1c] px-6 py-3 text-sm font-semibold text-[#9d1f1c] transition hover:bg-[#fff5f2] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "cancelling" && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancel monthly seva
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManageSubscription;
